import { RangeSlider } from '@/components/ui/slider'

import { SliderColor, SliderColorConfig } from './slider-color-config'

// RangeSlider のつまみの大きさ (px)。Radix はつまみが端からはみ出さないよう位置をずらすので、印も同じだけずらす
const THUMB_SIZE_PX = 20

/** トラック上の印。strong ほど太く長く出す */
export type ScrollbarMarker = { value: number; emphasis?: 'strong' | 'normal' | 'weak'; color?: string }

const MARKER_CLASS: Record<NonNullable<ScrollbarMarker['emphasis']>, { vertical: string; horizontal: string }> = {
  strong: { vertical: 'h-1 w-4', horizontal: 'h-4 w-1' },
  normal: { vertical: 'h-0.5 w-3', horizontal: 'h-3 w-0.5' },
  weak: { vertical: 'h-0.5 w-2', horizontal: 'h-2 w-0.5' },
}

type VerticalScrollbarProps = {
  scrollValue: number
  onScrollChange: (value: number) => void
  className?: string
  max?: number
  color?: SliderColor
  top?: string | number
  bottom?: string | number
  left?: string | number
  right?: string | number
  height?: string
  orientation?: 'vertical' | 'horizontal'
  style?: React.CSSProperties
  inverted?: boolean
  /** トラック上に付ける印（value は scrollValue と同じ単位）。AI の重要文があるページなど */
  markers?: ScrollbarMarker[]
  /** 印の色（CSS の色）。印ごとに color があればそちらを使う */
  markerColor?: string
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | 'className'>

export const CustomScrollbar = ({
  scrollValue,
  onScrollChange,
  className = '',
  max = 100,
  color = SliderColor.BLUE,
  top,
  bottom,
  left,
  right,
  height,
  orientation = 'vertical',
  style = {},
  inverted = false,
  markers,
  markerColor = '#ef4444',
  ...rest
}: VerticalScrollbarProps) => {
  const colorStyles = SliderColorConfig.getStyles(color)

  // 統一されたcontainerStyle
  const containerStyle = {
    ...(top !== undefined && { top }),
    ...(bottom !== undefined && { bottom }),
    ...(left !== undefined && { left }),
    ...(right !== undefined && { right }),
    ...(height !== undefined && { height }),
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    ...colorStyles,
    ...style,
  }

  // topとbottomが両方指定されている場合はh-fullを使わない（高さはtop/bottomで決まる）
  const hasVerticalBounds = top !== undefined && bottom !== undefined
  const hasHorizontalBounds = left !== undefined && right !== undefined

  const containerClasses =
    orientation === 'vertical'
      ? `fixed ps-5 pe-1 py-5 right-0 ${hasVerticalBounds ? '' : 'h-full'} flex justify-center items-center ${className}`
      : `fixed px-5 ${hasHorizontalBounds ? '' : 'left-0 w-full'} flex justify-center items-center ${className}`

  return (
    <div className={containerClasses} style={containerStyle} {...rest}>
      <div
        className={
          orientation === 'vertical' ? 'relative h-full w-6 flex items-center' : 'relative w-full h-6 flex items-center'
        }
      >
        <RangeSlider
          inverted={orientation === 'vertical' || (orientation === 'horizontal' && inverted)}
          orientation={orientation}
          className={`${orientation === 'vertical' ? 'h-full w-2' : 'w-full h-2'} [&_.bg-secondary]:bg-(--slider-track-color) [&_.bg-primary]:bg-(--slider-range-color) [&_.border-primary]:border-(--slider-thumb-border-color)`}
          min={0}
          max={max}
          step={1}
          value={[scrollValue]}
          onValueChange={(value) => onScrollChange(value[0])}
        />
        {markers && max > 0 && (
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            {markers.map((marker) => {
              const ratio = Math.min(Math.max(marker.value / max, 0), 1)
              const size = MARKER_CLASS[marker.emphasis ?? 'normal']
              // 縦は上が最小（inverted）、横は inverted なら右が最小
              const fromStart = orientation === 'horizontal' && inverted ? 1 - ratio : ratio
              const offset = `calc(${fromStart * 100}% + ${(0.5 - fromStart) * THUMB_SIZE_PX}px)`
              return (
                <span
                  key={marker.value}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${
                    orientation === 'vertical' ? `left-1/2 ${size.vertical}` : `top-1/2 ${size.horizontal}`
                  }`}
                  style={{
                    backgroundColor: marker.color ?? markerColor,
                    ...(orientation === 'vertical' ? { top: offset } : { left: offset }),
                  }}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
